/**
 * Review cadence → the window an attribution engine reads.
 *
 * The deep-dive's cadence filter picks the edition of the manager's review, and
 * the attribution beside it has to answer for the same stretch of time — a
 * monthly review sat over a trailing-twelve-month attribution was two different
 * answers to one question. So each cadence names a to-date window, the way a
 * desk quotes one:
 *
 *   daily      the latest day with a stored run
 *   monthly    month to date
 *   quarterly  quarter to date
 *   yearly     year to date
 *
 * `frequency` is the engine's own period for that window, so a to-date window is
 * ONE period and the backend links it exactly (Carino) — the effects then add up
 * to the active return instead of sitting beside a compounded figure.
 *
 * Daily looks back a week and keeps only the newest period (`latestOnly`): a run
 * is stored per trading day, so "today" on a weekend, a holiday or before the
 * nightly job would otherwise be an empty window.
 */

const pad = (n) => String(n).padStart(2, '0');

/**
 * A LOCAL calendar date as YYYY-MM-DD.
 *
 * `toISOString` is UTC, and in IST the 1st of a month at local midnight is still
 * the last day of the previous month in UTC — which would make every
 * month-to-date window open a day early.
 */
export const localIsoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Cadences the window follows, with the name the desk gives each. */
export const ATTRIBUTION_WINDOW_LABEL = {
  daily: 'Latest day',
  monthly: 'Month to date',
  quarterly: 'Quarter to date',
  yearly: 'Year to date',
};

export function attributionWindow(cadence, today = new Date()) {
  const end = localIsoDate(today);
  const y = today.getFullYear();
  const m = today.getMonth();

  switch (cadence) {
    case 'daily':
      return {
        cadence,
        label: ATTRIBUTION_WINDOW_LABEL.daily,
        frequency: 'daily',
        start: localIsoDate(new Date(y, m, today.getDate() - 7)),
        end,
        latestOnly: true,
      };
    case 'quarterly':
      return {
        cadence,
        label: ATTRIBUTION_WINDOW_LABEL.quarterly,
        frequency: 'quarterly',
        start: localIsoDate(new Date(y, m - (m % 3), 1)),
        end,
        latestOnly: false,
      };
    case 'yearly':
      return {
        cadence,
        label: ATTRIBUTION_WINDOW_LABEL.yearly,
        frequency: 'yearly',
        start: localIsoDate(new Date(y, 0, 1)),
        end,
        latestOnly: false,
      };
    case 'monthly':
    default:
      return {
        cadence: 'monthly',
        label: ATTRIBUTION_WINDOW_LABEL.monthly,
        frequency: 'monthly',
        start: localIsoDate(new Date(y, m, 1)),
        end,
        latestOnly: false,
      };
  }
}

/** "2026-09-15" → a local Date, never shifted by a UTC parse. */
const localDate = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A date range cut into consecutive periods at a frequency — each period a
 * `{ key, label, start, end }` window, the first and last clamped to the range.
 *
 * For reads that answer one window per call (the factor engine's linked
 * attribution), so a "monthly" view is one call per month. Daily periods are
 * weekdays only: the stored runs are trading days, and a weekend window would
 * only come back empty. Weeks start on Monday; quarters are calendar quarters.
 */
export function periodWindows(start, end, frequency = 'monthly') {
  if (!start || !end || start > end) return [];
  const first = localDate(start);
  const last = localDate(end);
  const out = [];
  const push = (a, b, label) => {
    const s = localIsoDate(a < first ? first : a);
    const e = localIsoDate(b > last ? last : b);
    if (s <= e) out.push({ key: s, label, start: s, end: e });
  };

  if (frequency === 'daily') {
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      if (d.getDay() === 0 || d.getDay() === 6) continue;
      push(new Date(d), new Date(d), localIsoDate(d));
    }
    return out;
  }
  if (frequency === 'weekly') {
    const d = new Date(first);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    for (; d <= last; d.setDate(d.getDate() + 7)) {
      const e = new Date(d);
      e.setDate(e.getDate() + 6);
      push(new Date(d), e, `Wk ${d.getDate()} ${MONTHS[d.getMonth()]}`);
    }
    return out;
  }
  if (frequency === 'quarterly') {
    for (let d = new Date(first.getFullYear(), first.getMonth() - (first.getMonth() % 3), 1); d <= last; d.setMonth(d.getMonth() + 3)) {
      push(new Date(d), new Date(d.getFullYear(), d.getMonth() + 3, 0), `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`);
    }
    return out;
  }
  if (frequency === 'yearly') {
    for (let y = first.getFullYear(); y <= last.getFullYear(); y += 1) {
      push(new Date(y, 0, 1), new Date(y, 11, 31), String(y));
    }
    return out;
  }
  for (let d = new Date(first.getFullYear(), first.getMonth(), 1); d <= last; d.setMonth(d.getMonth() + 1)) {
    push(new Date(d), new Date(d.getFullYear(), d.getMonth() + 1, 0), `${MONTHS[d.getMonth()]} ${d.getFullYear()}`);
  }
  return out;
}

/**
 * THE ONE WINDOW SET every attribution is read over.
 *
 * Three models answer "where did the return come from" — Brinson, the
 * fundamental style model and the regression model — and until now each screen
 * offered its own vocabulary for the period: the fund screen had MTD/QTD/YTD
 * and nine rolling lengths, the client screens had Today / MTD / QTD / YTD /
 * Since inception, and the style card had 1m/3m/6m/12m. Comparing two models
 * over "the same window" meant picking two things that were not the same, and
 * nothing on screen said so.
 *
 * Each entry carries BOTH forms because the three models are asked differently:
 *
 *   `cadence` — Brinson takes a named window (`useAttribution`), and only the
 *               to-date ones have a name. A rolling window has `cadence: null`
 *               and Brinson falls back to its dates.
 *   `months`  — a rolling length, counted back from today.
 *   `toDate`  — a to-date window: month, quarter or year start.
 *   `latest`  — the single most recent day, which the style model can answer as
 *               a snapshot and the others cannot.
 *
 * `inception` has neither: it means "everything", and the caller supplies the
 * start from whatever history its subject actually has.
 */
export const ATTRIBUTION_WINDOWS = [
  { id: 'daily', label: 'Latest day', cadence: 'daily', latest: true },
  { id: 'mtd', label: 'MTD', cadence: 'monthly', toDate: 'month' },
  { id: 'qtd', label: 'QTD', cadence: 'quarterly', toDate: 'quarter' },
  { id: 'ytd', label: 'YTD', cadence: 'yearly', toDate: 'year' },
  { id: '1m', label: '1M', months: 1 },
  { id: '3m', label: '3M', months: 3 },
  { id: '6m', label: '6M', months: 6 },
  { id: '1y', label: '1Y', months: 12 },
  { id: '3y', label: '3Y', months: 36 },
  { id: '5y', label: '5Y', months: 60 },
  { id: 'inception', label: 'Since inception', cadence: 'inception' },
];

export const ATTRIBUTION_WINDOW_IDS = ATTRIBUTION_WINDOWS.map((w) => w.id);

/**
 * One of those windows as concrete dates.
 *
 * `inception` needs a start from the caller — the subject's own first valued
 * day — because nothing here knows how far back a given fund or account goes.
 * Given none it falls back to ten years, which is what the resolvers do with an
 * unbounded range anyway.
 */
export function attributionRange(id, { today = new Date(), inception = null } = {}) {
  const w = ATTRIBUTION_WINDOWS.find((x) => x.id === id) ?? ATTRIBUTION_WINDOWS[3];
  const end = localIsoDate(today);
  const y = today.getFullYear();
  const m = today.getMonth();

  if (w.latest) return { ...w, start: end, end };
  if (w.toDate === 'month') return { ...w, start: localIsoDate(new Date(y, m, 1)), end };
  if (w.toDate === 'quarter') return { ...w, start: localIsoDate(new Date(y, m - (m % 3), 1)), end };
  if (w.toDate === 'year') return { ...w, start: localIsoDate(new Date(y, 0, 1)), end };
  if (w.months) return { ...w, start: localIsoDate(new Date(y, m - w.months, today.getDate())), end };
  return { ...w, start: inception || localIsoDate(new Date(y - 10, m, today.getDate())), end };
}
